import { Injectable, NgZone } from '@angular/core';

// external libs
import { Observable, Observer } from 'rxjs';

// services
import { MarkerClusterService } from '../abstract/abstract-marker-cluster';

// directives
import { H21MapMarkerClusterDirective } from '../../directives/h21-map-marker-cluster.directive';

// models
import { FeatureEvent } from '../../models/feature-event.model';
import { Position } from '../../models/position.model';
import { CircleEvent } from '../../models/circle-event.model';
import { MarkerClusterFilter } from '../../models/marker-cluster-filter.model';
import { ChangesMap } from '../../enums/changes-map.enum';

@Injectable()
export class YandexDataLayerService extends MarkerClusterService<ymaps.Map, ymaps.GeoObject, ymaps.Polyline> {

    private layers: Map<H21MapMarkerClusterDirective, ymaps.ObjectManager> = new Map<H21MapMarkerClusterDirective, ymaps.ObjectManager>();
    private _layer: H21MapMarkerClusterDirective;

    constructor(private _zone: NgZone) {
        super();
    }

    public updateMarkerCluster(layer: H21MapMarkerClusterDirective): void {
        this.removeMarkerCluster(layer);
        this.createOverlay(layer);
        this.ready$.next();
        this.getCountLoadMarkers(layer);
    }

    public addMarkerCluster(layer?: H21MapMarkerClusterDirective): void {
        this._createDataLayer().subscribe((dataLayer) => {
            this._layer = layer;
            this.layers.set(layer, dataLayer);
            this.map.api.geoObjects.add(dataLayer);
            this.createOverlay(layer);
        });
    }

    public createOverlay(layer?: H21MapMarkerClusterDirective): void {
        if (!layer.geoJson) { return; }
        this._getArray(layer).subscribe((() => {
            this._addDataLayer(layer);
        }));
    }

    public removeMarkerClusters(): void {
        this.layers.forEach((key) => {
            key.removeAll();
        });
        this.layers.clear();
    }

    public removeFeature(layer: H21MapMarkerClusterDirective): void {
        this.removeMarkerCluster(layer);
    }

    public createEvent<E>(eventName: string, layer: H21MapMarkerClusterDirective): Observable<E> {
        return new Observable((observer: Observer<any>) => {
            this.layers.get(layer).objects.events.add(eventName, (event) => {
                event.stopPropagation();
                this._zone.run(() => observer.next(event));
            });
            this.layers.get(layer).clusters.events.add(eventName, (event) => {
                event.stopPropagation();
                this._zone.run(() => observer.next(event));
            });
        });
    }
    public createEventMouseOver<D>(layer: H21MapMarkerClusterDirective): Observable<D> {
        return new Observable((observer: Observer<any>) => {
            this.createEvent('mouseenter', layer).subscribe((event: any) => {
                event.isSelected = true;
                this._overrideStyle(layer, event);
                this._zone.run(() => observer.next((this._createEventField(layer, event))));
            });
        });
    }

    public createEventMouseOut<D>(layer: H21MapMarkerClusterDirective): Observable<D> {
        return new Observable((observer: Observer<any>) => {
            this.createEvent('mouseleave', layer).subscribe((event: any) => {
                event.isSelected = false;
                this._overrideStyle(layer, event);
                this._zone.run(() => observer.next((null)));
            });
        });
    }

    public createEventMouseClick<DE>(layer: H21MapMarkerClusterDirective): Observable<DE> {
        return new Observable((observer: Observer<any>) => {
            this.createEvent('click', layer).subscribe((event: any) => {
                this._clusterFitbounds(event);
                event.stopPropagation();
                this._zone.run(() => observer.next((this._createEventField(layer, event))));
            });
        });
    }

    public removeMarkerCluster(layer?: H21MapMarkerClusterDirective): void {
        try {
            this.layers.get(layer) && this.layers.get(layer).removeAll();
        } catch { }
    }

    public setModeCircle(CircleOptions: CircleEvent): void {
        this.modeCircle.next(CircleOptions);
    }

    public setFilter(filter: MarkerClusterFilter): void {
        this.changeFilter.next(filter);
    }

    public updateStyle(id: number, position): void {
        this._redrawStyle();
        try {
            const layer = this.layers.get(this._layer) &&
                this.layers.get(this._layer).objects.getAll().find((val) => val.id === id);

            (layer && layer.type === 'Cluster')
                && this.layers.get(this._layer).clusters.setClusterOptions(layer.id, {
                    clusterIconLayout: ymaps.templateLayoutFactory.createClass(`<div class="${layer.properties.iconClass}${' '
                        }${layer.properties.iconClassSelected}">${''
                        }${layer.properties.iconContent}</div>`),

                });

            (layer && layer.type === 'Feature')
                && this.layers.get(this._layer).objects.setObjectOptions(layer.id, {
                    iconImageHref: '/',
                    iconContentLayout: ymaps.templateLayoutFactory.createClass(`<div class="${layer.properties.iconClassSelected}">${''
                        }${layer.properties.iconContent}</div>`),
                });

            (!layer) && this.isMarkerCluster.next(position);


        } catch { }
    }

    public clearMarkerCluster(): void {
        this.removeFeature(this._layer);
        this.clearMarkerCluster$.next();
    }

    public getBounds(): any { }

    private _createDataLayer(): Observable<any> {
        return new Observable((observer: Observer<any>) => {
            const options = {
                clusterDisableClickZoom: true,
                clusterHideIconOnBalloonOpen: false,
                geoObjectHideIconOnBalloonOpen: false,
                zIndex: 1001,
                groupByCoordinates: false,
                hasBalloon: false,
            };
            observer.next(new ymaps.ObjectManager(options));
        });
    }

    private _redrawStyle(): void {
        this.layers.get(this._layer).objects.each((feature) => {
            const typeFeature = feature.type;
            typeFeature === 'Cluster' ?
                this.layers.get(this._layer).clusters.setClusterOptions(feature.id, this._createOptionsCluster(feature))
                : this.layers.get(this._layer).objects.setObjectOptions(feature.id, this._createOptionsFeature(feature));
        });
    }

    private _createOverlay(layer: H21MapMarkerClusterDirective): any {
        this.layers.get(layer) && this.layers.get(layer).objects.each((feature) => {
            const typeFeature = feature.type;
            typeFeature === 'Cluster' ?
                this.layers.get(layer)
                && this.layers.get(layer).clusters.setClusterOptions(feature.id, this._createOptionsCluster(feature))
                : this.layers.get(layer)
                && this.layers.get(layer).objects.setObjectOptions(feature.id, this._createOptionsFeature(feature));
        });
    }

    private _createTemplateFeature(feature: any, isSelected?: boolean): string {
        const iconClass = isSelected ? feature.properties.iconClassSelected : feature.properties.iconClass;
        return `<div class="${iconClass}">${feature.properties.iconContent}</div>`;
    }

    private _createTemplateCluster(feature: any): string {
        return `<div class="${feature.properties.iconClass}">${feature.properties.iconContent}</div>`;
    }

    private _createOptionsCluster(feature: any): any {
        return {
            clusterIconShape: {
                type: 'Circle',
                coordinates: [0, 0],
                radius: 25,
            },
            clusterIconLayout: ymaps.templateLayoutFactory.createClass(this._createTemplateCluster(feature)),
            clusterIconLayoutSize: [54, 54],
            clusterDisableClickZoom: true,
            clusterHideIconOnBalloonOpen: false,
            geoObjectHideIconOnBalloonOpen: false,
            zIndex: feature.properties.zIndex,
            groupByCoordinates: false,
            hasBalloon: false,
        };
    }

    private _createOptionsFeature(feature: any, isSelected?: boolean): any {
        return {
            iconLayout: feature.properties.iconUrl ? 'default#image' : 'default#imageWithContent',
            iconImageSize: feature.properties.iconUrl ? [feature.properties.iconSize[0], feature.properties.iconSize[1]] :
                [81, 30],
            iconImageOffset: feature.properties.iconUrl ? [-7, -10] : [-40, -20],
            iconContentOffset: [40, 0],
            syncOverlayInit: true,
            iconImageHref: feature.properties.iconUrl ? feature.properties.iconUrl : '/',
            zIndex: feature.properties.zIndex,
            zIndexHover: feature.properties.zIndexSelected,
            showHintOnHover: false,
            hasBalloon: false,
            hasHint: false,
            iconContentLayout: ymaps.templateLayoutFactory.createClass(this._createTemplateFeature(feature)),
        };
    }

    private _addDataLayer(layer: H21MapMarkerClusterDirective): void {
        this.layers.get(layer) && this.layers.get(layer).add(layer.geoJson);
        this._createOverlay(layer);
    }

    private _getArray(layer?: H21MapMarkerClusterDirective): Observable<any> {
        return new Observable((observer) => {
            layer.geoJson && layer.geoJson.features.forEach((feature) => {
                const _geometry = feature.geometry.coordinates;
                feature.geometry.coordinates = [_geometry[1], _geometry[0]];
            });
            observer.next();
            observer.complete();
        });
    }

    private _overrideStyle(layer: H21MapMarkerClusterDirective, event: any) {
        const _objectId = event.get('objectId');
        const _layer = this.layers.get(layer);
        const _feature = _layer.objects.getById(_objectId);
        if (!_feature) { return; }
        let _icon = _feature.properties.iconUrl;
        event.isSelected ? _icon = _feature.properties.iconUrlSelected
            : _icon = _feature.properties.iconUrl;

        this.layers.get(layer).objects.setObjectOptions(_objectId, {
            iconImageHref: _icon ? _icon : '/',
            iconContentLayout: _feature.properties.iconUrl ? null
                : ymaps.templateLayoutFactory.createClass(this._createTemplateFeature(_feature, Boolean(event.isSelected))),
        });
    }

    private _createEventField(layer: H21MapMarkerClusterDirective, event: any): FeatureEvent {
        const _objectId = event.get('objectId');
        const _layer = this.layers.get(layer);
        const _feature = _layer.objects.getById(_objectId);
        event.geometry = _feature.geometry.coordinates;
        this._eventPositionIcon(event, _feature);
        const pixel = this.map.conversions.translateLatLngClientXY(event);
        return new FeatureEvent(
            new Position(_feature.geometry.coordinates[1], _feature.geometry.coordinates[0]),
            pixel.clientX,
            pixel.clientY,
            _feature.properties.data || null,
            _feature.type,
        );
    }

    private _eventPositionIcon(event: any, feature) {
        feature.properties.iconUrl ? event.offsetX = 5 : event.offsetX = 0;
        feature.properties.iconUrl ? event.offsetY = 17 : event.offsetY = 10;
        return event;
    }

    private _clusterFitbounds(event: any): void {
        const type = event.getSourceEvent().originalEvent.overlay._data.type;
        if (type === 'Cluster') {
            const feature = event.getSourceEvent().originalEvent.target.getById(event.get('objectId'));
            const n = feature._data.bbox[0];
            const s = feature._data.bbox[1];

            const bounds = [[n[1], n[0]], [s[1], s[0]]];
            const options = {
                checkZoomRange: false,
                useMapMargin: true,
                duration: 400,
            };
            this.map.api.setBounds(bounds, options);
        }
    }

}
